import { useMemo, useState } from "react";
import { useData } from "../../../context/DataContext.jsx";
import { C, font, mono, card } from "../../../lib/styles.js";
import { JOB_STATUS_COLORS, SESSION_TYPE_COLORS, SESSION_TYPE_LABELS, DEFAULT_SESSION_TYPE } from "../../../lib/constants.js";
import { describeHoldDates, scheduleEntries } from "../../../lib/holds.js";
import { entriesByDate, fromISO, isSameMonth, monthGrid, monthTitle, shiftMonth, spannedKeys, toISO, weekSpans } from "../../../lib/scheduleGrid.js";
import { jobAccess } from "../lib/jobAccess.js";
import { useIsMobile } from "../lib/useIsMobile.js";
import { UpcomingJobsList } from "./Dashboard.jsx";
import { ChipWithBadge, HoldChip, LegendConflict, LegendHold, LegendJob, HOLD_COLOR, bangStyle, chipLabelFor, holdAriaLabel, holdTooltip, jobChipStyle } from "./ScheduleBits.jsx";

/* Jobs > Schedule (docs/holds/holds-plan.md, decision 6, Frame E): one month,
   Monday first, six rows. Jobs are solid chips, holds dashed chips, a multi-day
   run draws as one bar per week row, a conflict is the amber badge. Clicking a
   day opens the same day panel the Dashboard shows. Every role sees the same
   rows, holds named by client and subject like bookings (Ivin, 2026-09-05);
   crew and viewers see no legend and never any money. Reads hold RECORDS plus
   jobs; admins also pass their quotes so a record written before the identity
   fields still prints its client. */

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BAR_H = 14;
const BAR_GAP = 2;
const BAR_TOP = 22;

export default function ScheduleView({ navigate, user }) {
  const { jobs, savedQuotes = [], holds = [], role: dataRole, crewRoster = [], equipmentMap = null } = useData();
  const isMobile = useIsMobile();
  const nav = navigate || (() => {});
  const access = jobAccess(dataRole || user?.role);
  const canSeeAdminMeta = access.canManageJobs;
  const JSC = JOB_STATUS_COLORS(C);
  const STC = SESSION_TYPE_COLORS(C);

  // Today is read on every render, not memoized at mount: a tab left open overnight
  // moves its highlight and its Today control to the new day on the next render.
  const todayStr = toISO(new Date());
  const [cursor, setCursor] = useState(() => ({ year: Number(todayStr.slice(0, 4)), month: Number(todayStr.slice(5, 7)) }));
  const [selectedDate, setSelectedDate] = useState(null);

  const weeks = useMemo(() => monthGrid(cursor.year, cursor.month), [cursor]);

  // Records carry their own client name; the quotes join is the fallback for a
  // record written before 2026-09-05 (crew browsers hold no quotes to join).
  const schedule = useMemo(() =>
    scheduleEntries(holds, jobs, crewRoster, { equipmentMap, quotes: canSeeAdminMeta ? savedQuotes : null }),
  [holds, jobs, crewRoster, equipmentMap, savedQuotes, canSeeAdminMeta]);
  const byDate = useMemo(() => entriesByDate(schedule.entries), [schedule]);
  const jobById = useMemo(() => new Map((jobs || []).map(j => [j.id, j])), [jobs]);

  // Per week row: the bars, and the chips those bars replace.
  const rows = useMemo(() => weeks.map(week => {
    const inWeek = week.flatMap(iso => byDate.get(iso) || []);
    const spans = weekSpans(inWeek, week);
    return { week, spans, spanned: spannedKeys(spans) };
  }), [weeks, byDate]);

  // The day panel reads the jobs on the selected date (from their entries) and
  // the hold entries on it.
  const dayEntries = useMemo(() => (selectedDate ? (byDate.get(selectedDate) || []) : []), [selectedDate, byDate]);
  const dayJobs = useMemo(() => {
    const seen = new Set();
    const out = [];
    dayEntries.forEach(e => {
      if (e.kind !== "job") return;
      const id = e.owner.slice("job:".length);
      if (seen.has(id)) return;
      seen.add(id);
      const job = jobById.get(id);
      if (job) out.push(job);
    });
    return out;
  }, [dayEntries, jobById]);
  const dayHolds = dayEntries.filter(e => e.kind === "hold");

  const move = (delta) => {
    setCursor(c => shiftMonth(c.year, c.month, delta));
    setSelectedDate(null);
  };
  const goToday = () => {
    setCursor({ year: Number(todayStr.slice(0, 4)), month: Number(todayStr.slice(5, 7)) });
    setSelectedDate(todayStr);
  };
  const toggleDay = (iso) => setSelectedDate(d => (d === iso ? null : iso));
  // Day cells are buttons: Enter and Space select the day, like a click.
  const onDayKey = (ev, iso) => {
    if (ev.key !== "Enter" && ev.key !== " ") return;
    ev.preventDefault();
    toggleDay(iso);
  };
  const dayLabel = (iso) => fromISO(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  const chipTitle = (e) => {
    if (e.kind === "hold") return holdTooltip(e);
    const typeLabel = SESSION_TYPE_LABELS[e.kinds?.[0]] || SESSION_TYPE_LABELS[DEFAULT_SESSION_TYPE];
    return `${typeLabel} · ${e.clientName || e.label}`;
  };
  const chipColor = (e) => {
    const kind = e.kinds?.[0] || DEFAULT_SESSION_TYPE;
    const job = jobById.get(e.owner.slice("job:".length));
    return STC[kind] || JSC[job?.status] || C.muted;
  };
  const renderChip = (e) => {
    const conflicted = schedule.conflicts.has(e.key);
    if (e.kind === "hold") return <HoldChip key={e.key} entry={e} conflicted={conflicted} title={chipTitle(e)} />;
    const kind = e.kinds?.[0] || DEFAULT_SESSION_TYPE;
    const faded = kind === "setup" || kind === "strike";
    return (
      <ChipWithBadge key={e.key} conflicted={conflicted}>
        <div data-testid="cal-chip-job" data-key={e.key} title={chipTitle(e)} style={jobChipStyle(chipColor(e), faded)}>{chipLabelFor(e)}</div>
      </ChipWithBadge>
    );
  };

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
      <div data-testid="schedule-title" style={{ fontSize: 16, fontWeight: 500, letterSpacing: "-0.01em" }}>{monthTitle(cursor.year, cursor.month)}</div>
      <div style={{ display: "flex", gap: 6 }}>
        <button type="button" data-testid="schedule-prev" aria-label="Previous month" onClick={() => move(-1)} style={S.navBtn}>‹</button>
        <button type="button" data-testid="schedule-today" onClick={goToday} style={S.navBtn}>Today</button>
        <button type="button" data-testid="schedule-next" aria-label="Next month" onClick={() => move(1)} style={S.navBtn}>›</button>
      </div>
    </div>
  );

  const legend = canSeeAdminMeta ? (
    <div data-testid="schedule-legend" style={{ display: "flex", gap: 14, justifyContent: "flex-end", marginTop: 8 }}>
      <LegendJob />
      <LegendHold />
      <LegendConflict />
    </div>
  ) : null;

  const panel = selectedDate ? (
    <UpcomingJobsList jobs={dayJobs} holds={dayHolds} schedule={schedule} equipmentMap={equipmentMap}
      isCrew={access.isCrew} canSeeMoney={access.canSeeMoney} canUseQuotes={access.canUseQuotes}
      JSC={JSC} STC={STC} selectedDate={selectedDate} onClear={() => setSelectedDate(null)} todayStr={todayStr} nav={nav} />
  ) : (
    <div data-testid="schedule-hint" style={{ fontSize: 12, color: C.muted, padding: "4px 2px" }}>Select a day to see its jobs and holds.</div>
  );

  if (isMobile) {
    // A phone gets the month as a list of the days that have something on them.
    const monthDays = weeks.flat().filter(iso => isSameMonth(iso, cursor.year, cursor.month) && (byDate.get(iso) || []).length > 0);
    return (
      <div style={{ animation: "fadeUp 0.3s ease" }}>
        {header}
        <div data-testid="schedule-list" style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 14 }}>
          {monthDays.length === 0 ? (
            <div style={{ padding: "24px 16px", textAlign: "center", color: C.muted, fontSize: 12 }}>Nothing scheduled this month.</div>
          ) : monthDays.map((iso, i) => {
            const d = fromISO(iso);
            const isSelected = selectedDate === iso;
            const isToday = iso === todayStr;
            return (
              <div key={iso}>
                <div data-testid="schedule-day" data-date={iso} role="button" tabIndex={0} aria-pressed={isSelected} aria-label={dayLabel(iso)}
                  onClick={() => toggleDay(iso)} onKeyDown={(ev) => onDayKey(ev, iso)} style={{
                    display: "grid", gridTemplateColumns: "50px 1fr", gap: 12, alignItems: "center", padding: "10px 14px",
                    cursor: "pointer", background: isSelected ? `${C.blue}14` : "transparent",
                  }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.1em", color: isToday ? C.amber : C.muted }}>{DOW[(d.getDay() + 6) % 7]}</div>
                    <div style={{ fontSize: 17, fontWeight: isToday ? 500 : 400, color: isToday ? C.amber : C.text }}>{d.getDate()}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    {(byDate.get(iso) || []).map(renderChip)}
                  </div>
                </div>
                {i < monthDays.length - 1 && <div style={{ height: 1, background: C.border, margin: "0 14px" }} />}
              </div>
            );
          })}
        </div>
        {panel}
      </div>
    );
  }

  return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>
      <div data-testid="schedule-grid" style={{ ...card, padding: "14px 16px 12px", marginBottom: 14 }}>
        {header}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", paddingBottom: 4 }}>
          {DOW.map(d => (
            <div key={d} style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.1em", color: C.muted, paddingLeft: 6 }}>{d}</div>
          ))}
        </div>
        {rows.map(({ week, spans, spanned }) => {
          const barsH = spans.length ? spans.length * (BAR_H + BAR_GAP) : 0;
          return (
            <div key={week[0]} data-testid="schedule-week" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", position: "relative", borderTop: `1px solid ${C.border}` }}>
              {week.map((iso, ci) => {
                const d = fromISO(iso);
                const inMonth = isSameMonth(iso, cursor.year, cursor.month);
                const isWeekend = ci >= 5;
                const isToday = iso === todayStr;
                const isSelected = selectedDate === iso;
                const cellEntries = (byDate.get(iso) || []).filter(e => !spanned.has(e.key));
                return (
                  <div key={iso} data-testid="schedule-day" data-date={iso} role="button" tabIndex={0} aria-pressed={isSelected} aria-label={dayLabel(iso)}
                    onClick={() => toggleDay(iso)} onKeyDown={(ev) => onDayKey(ev, iso)} style={{
                    minHeight: 64 + barsH, padding: "4px 6px 6px", borderLeft: ci === 0 ? "none" : `1px solid ${C.border}`,
                    display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start", cursor: "pointer",
                    background: isSelected ? `${C.blue}14` : isWeekend ? C.bg : "transparent",
                    boxShadow: isSelected ? `inset 0 0 0 1px ${C.blue}38` : "none",
                    minWidth: 0,
                  }}>
                    <span style={{
                      fontSize: 11, fontWeight: isToday ? 500 : 400,
                      color: isToday ? C.amber : inMonth ? C.text : `${C.muted}8c`,
                      width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center",
                      borderRadius: 10, background: isToday ? `${C.amber}26` : "transparent",
                    }}>{d.getDate()}</span>
                    {barsH > 0 && <div style={{ height: barsH - BAR_GAP, flex: "none" }} />}
                    {cellEntries.slice(0, 3).map(renderChip)}
                    {cellEntries.length > 3 && <div style={{ fontSize: 7.5, color: C.muted }}>+{cellEntries.length - 3}</div>}
                  </div>
                );
              })}
              {spans.map((bar, bi) => {
                // Grid-fraction geometry: no pixel widths, so the bar tracks the
                // cells at every viewport.
                const cols = bar.endCol - bar.startCol + 1;
                const job = bar.kind === "job" ? jobById.get(bar.owner.slice("job:".length)) : null;
                const clr = bar.kind === "hold" ? HOLD_COLOR : (JSC[job?.status] || C.blue);
                const isHold = bar.kind === "hold";
                // "Hold · Brooks Films · Oct 26 to Oct 28" or "pier59 · Oct 27 to Oct 28".
                const text = `${chipLabelFor(bar)} · ${describeHoldDates(bar.dates)}`;
                const title = isHold ? holdTooltip(bar, bar.dates) : text;
                const aria = isHold ? holdAriaLabel(bar, bar.dates) : `${bar.label}, ${describeHoldDates(bar.dates)}`;
                // A bar wears the same badge as a chip when any day it covers collides.
                const conflicted = bar.keys.some(k => schedule.conflicts.has(k));
                // A bar is a button like the cells: click, Enter and Space all activate it.
                // A hold bar selects its first day; a job bar opens the job, as its click does.
                const activate = () => {
                  if (job) nav("jobs", job.id);
                  else setSelectedDate(bar.dates[0]);
                };
                const onClick = (ev) => {
                  ev.stopPropagation();
                  activate();
                };
                const onKey = (ev) => {
                  if (ev.key !== "Enter" && ev.key !== " ") return;
                  ev.preventDefault();
                  ev.stopPropagation();
                  activate();
                };
                return (
                  <div key={`${bar.owner}-${bar.startCol}`} data-testid="schedule-bar" data-kind={bar.kind} data-owner={bar.owner}
                    role="button" tabIndex={0} aria-label={aria}
                    title={title} onClick={onClick} onKeyDown={onKey}
                    style={{
                      position: "absolute", top: BAR_TOP + bi * (BAR_H + BAR_GAP), height: BAR_H,
                      left: `calc(${bar.startCol} * 100% / 7 + 4px)`, width: `calc(${cols} * 100% / 7 - 8px)`,
                      zIndex: 2, cursor: "pointer",
                    }}>
                    <div data-testid="schedule-bar-body" style={{
                      boxSizing: "border-box", width: "100%", height: "100%",
                      border: bar.kind === "hold" ? `1px dashed ${clr}a0` : `1px solid ${clr}59`,
                      background: bar.kind === "hold" ? "transparent" : `${clr}1a`,
                      borderRadius: 4, color: clr, fontFamily: mono, fontSize: 8, fontWeight: 500, lineHeight: "12px",
                      padding: "0 5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", letterSpacing: "0.02em",
                    }}>{text}</div>
                    {conflicted && <span data-testid="cal-conflict-badge" title="Conflict" style={{ ...bangStyle, position: "absolute", top: -5, right: -6 }}>!</span>}
                  </div>
                );
              })}
            </div>
          );
        })}
        {legend}
      </div>
      {panel}
    </div>
  );
}

const S = {
  navBtn: {
    padding: "5px 12px", background: "transparent", color: C.muted,
    border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12,
    cursor: "pointer", fontFamily: font, fontWeight: 400, lineHeight: 1.2,
  },
};
